import React, { useEffect, useRef } from 'react';
import { Animated, Easing } from 'react-native';
import { useThemeColors } from '@/hooks/residents/useThemeColors';

interface SpinnerProps {
  size?: number;
  strokeWidth?: number;
  color?: string;
  trackColor?: string;
}

/**
 * A minimal, brand-colored rotating ring — a faint full-circle "track" plus a
 * single accent border (top) that reads as a smooth spinner when rotated. No
 * SVG: a plain View border-trick.
 *
 * Uses React Native's legacy `Animated.loop` (NOT reanimated) so it rotates
 * continuously on both native AND react-native-web — the web reanimated shim's
 * `withRepeat` is a no-op passthrough, so a reanimated loop would sit frozen on
 * desktop. Always spins: an essential activity indicator must rotate even under
 * OS Reduce Motion.
 */
export const Spinner: React.FC<SpinnerProps> = ({
  size = 40,
  strokeWidth = 3,
  color,
  trackColor,
}) => {
  const { semantic, colors } = useThemeColors();
  const rotation = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    const animation = Animated.loop(
      Animated.timing(rotation, {
        toValue: 1,
        duration: 900,
        easing: Easing.linear,
        useNativeDriver: true,
      }),
    );
    animation.start();
    return () => animation.stop();
  }, [rotation]);

  const spin = rotation.interpolate({
    inputRange: [0, 1],
    outputRange: ['0deg', '360deg'],
  });

  return (
    <Animated.View
      accessibilityElementsHidden
      importantForAccessibility="no-hide-descendants"
      style={{
        width: size,
        height: size,
        borderRadius: size / 2,
        borderWidth: strokeWidth,
        borderColor: trackColor ?? colors.glass.buttonBorder,
        borderTopColor: color ?? semantic.interactive.primary,
        transform: [{ rotate: spin }],
      }}
    />
  );
};
